'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link href="/" className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#3A6CF0]">Cortic</span>
            <span className="text-sm text-gray-500 hidden sm:inline">Consulting</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              href="/"
              className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
            >
              Home
            </Link>
            <Link 
              href="/about"
              className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
            >
              About
            </Link>
            <Link 
              href="/insights"
              className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
            >
              Insights
            </Link>
            <Link 
              href="/contact"
              className="px-4 py-2 rounded-md bg-[#0F62FE] text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Contact
            </Link>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            type="button"
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-label="Open main menu"
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>

      {/* Mobile Menu — Overlay + Sidebar */}
      <div className="md:hidden">
        {/* Backdrop */}
        <div
          className={cn(
            "fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300",
            isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          )}
          onClick={closeMenu}
          aria-hidden="true"
        />

        {/* Sidebar Panel */}
        <div
          className={cn(
            "fixed top-0 right-0 z-[70] h-[100dvh] w-[80%] max-w-sm bg-white shadow-2xl",
            "flex flex-col",
            "transform transition-transform duration-300 ease-in-out",
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between px-6 h-16 border-b border-gray-100 flex-shrink-0">
            <Link href="/" onClick={closeMenu}>
              <span className="text-xl font-bold text-[#3A6CF0]">Cortic</span>
            </Link>
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Sidebar Links */}
          <nav className="flex flex-col px-6 py-8 space-y-1 flex-grow">
            <Link
              href="/"
              onClick={closeMenu}
              className="text-base font-medium text-gray-700 hover:text-[#3A6CF0] hover:bg-blue-50 rounded-md px-3 py-3 transition-colors"
            >
              Home
            </Link>
            <Link
              href="/about"
              onClick={closeMenu}
              className="text-base font-medium text-gray-700 hover:text-[#3A6CF0] hover:bg-blue-50 rounded-md px-3 py-3 transition-colors"
            >
              About
            </Link>
            <Link
              href="/insights"
              onClick={closeMenu}
              className="text-base font-medium text-gray-700 hover:text-[#3A6CF0] hover:bg-blue-50 rounded-md px-3 py-3 transition-colors"
            >
              Insights
            </Link>
          </nav>

          {/* Sidebar CTA */}
          <div className="px-6 py-6 border-t border-gray-100 flex-shrink-0">
            <Link
              href="/contact"
              onClick={closeMenu}
              className="block w-full text-center px-4 py-3 rounded-md bg-[#0F62FE] text-white font-medium hover:bg-blue-700 transition-colors"
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
